import { useState, useEffect } from 'react';
import styled from 'styled-components';

import { ClickAwayListener, Box, Typography } from '@mui/material';
import Label from './label';

import { ReactComponent as ArrowIcon } from '../../assets/icons/arrow-icon.svg';
import { ReactComponent as ClearIcon } from '../../assets/icons/clear-icon.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/search-icon.svg';

import { colors } from '../../theme';

interface ContainerProps {
    disabled: boolean;
    optionOpen: boolean;
    width: string;
    multiSelect: boolean;
    isFilter: boolean;
}

interface OptionProps {
    isLast: boolean;
    selectedItem: boolean;
}

const GlobalDropdown = (props: any) => {
    const {
        data,
        placeholder,
        label,
        value,
        name,
        field,
        onChange,
        disabled,
        isSearch,
        width,
        table,
        multiSelect,
        handleRemove,
        icon,
        isFilter,
        filterItems,
        dataItemKey,
        onCreate,
        sx,
        isRenderData,
        renderData,
        isRenderValue,
        renderValue,
        searchField,
        additionalSearchField,
        isManualClose,
        onClick,
    } = props;

    const [state, setState] = useState({
        searchItem: null,
        filteredOptions: [],
        createItem: null,
    });

    const [optionOpen, setOptionOpen] = useState(false);
    const [fieldName, setFieldName] = useState(name);

    const inputClicked = 'inputClicked';
    const created = 'created';
    const removed = 'removed';

    const handleItemClick = (item) => {
        let itemArr = [];
        if (multiSelect) {
            if (value?.length > 0) {
                itemArr = [...value];
            }
            if (itemArr.indexOf(item) !== -1) return;
            itemArr.push(item);
            onChange({
                target: {
                    name: dataItemKey,
                    value: itemArr,
                },
            });
        } else {
            onChange(dataItemKey, item);
        }

        setOptionOpen((state) => !state);
    };

    const handleInputChange = (event) => {
        const {
            target: { name, value },
        } = event;

        const searchTerm = value.toLowerCase();

        const filteredOptions = data.filter((d) => {
            if (isRenderData && renderData && typeof renderData === 'function') {
                const displayValue = renderData(d);
                return displayValue.toLowerCase().includes(searchTerm);
            }

            if (additionalSearchField) {
                const searchItem = `${d[searchField]?.[additionalSearchField] || ''} - ${d[fieldName] || ''}`;
                return searchItem.toLowerCase().includes(searchTerm);
            }

            return d[fieldName]?.toLowerCase().includes(searchTerm);
        });

        setState({
            ...state,
            [name]: value,
            filteredOptions,
        });
    };

    const clearSearchInput = () => {
        setState({
            ...state,
            searchItem: '',
            filteredOptions: data,
        });
    };

    useEffect(() => {
        if (data) {
            setState({
                ...state,
                filteredOptions: data,
            });
        }
    }, [data]);

    const handleOpen = (type) => {
        if (isSearch) {
            if (type === inputClicked || type === removed) {
                setOptionOpen(true);
            } else if (type === created) setOptionOpen(false);
            setOptionOpen((state) => !state);
        } else setOptionOpen((state) => !state);
    };

    const handleClickAway = () => {
        setOptionOpen(false);
        clearSearchInput();
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway} >
            <Box sx={{ display: 'flex', alignItems: 'center', ...sx }} onClick={onClick}>
                {label && <Label disabled={disabled} icon={icon} label={label} {...props} />}
                <Container
                    disabled={disabled}
                    optionOpen={optionOpen}
                    width={width}
                    isFilter={isFilter}
                    multiSelect={multiSelect && value?.length > 0}
                    onClick={() => {
                        if (disabled) return;
                        handleOpen('');
                    }}
                >
                    <Typography sx={{ mr: '5px', display: 'flex', flexWrap: 'wrap' }} variant={table ? 'body2' : 'body1'}>
                        {multiSelect && value.length > 0
                            ? value.map((o, i) => {
                                  return (
                                      <SelectedItem key={i}>
                                          {isRenderValue ? renderValue(o) : o[name]}
                                          <ClearIcon
                                              onClick={() => {
                                                  if (multiSelect) {
                                                      handleRemove(o, i);
                                                      handleOpen(removed);
                                                  }
                                              }}
                                          />
                                      </SelectedItem>
                                  );
                              })
                            : !multiSelect && value && name && data.findIndex((o) => o[field] === value[field]) >= 0
                            ? isRenderValue 
                                ? renderValue(data[data.findIndex((o) => o[field] === value[field])]) 
                                    : data[data.findIndex((o) => o[field] === value[field])][name]
                            : !name && value
                            ? value
                            : placeholder}
                    </Typography>

                    <ArrowIcon id="arrow" />
                    {optionOpen &&
                        (isFilter ? (
                            <OptionContainer className="dropdown-option-container">
                                {filterItems.map((item: any, i: any) => {
                                    return (
                                        <FilterOption key={i} {...props}>
                                            <Typography variant="body2">{item.title}</Typography>
                                            <div>
                                                {item.options.map((o: any, i: any) => {
                                                    return (
                                                        <span key={i}>
                                                            <Typography variant="body2">{o[name]}</Typography>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </FilterOption>
                                    );
                                })}
                            </OptionContainer>
                        ) : (
                            <OptionContainer className="dropdown-option-container">
                                {isSearch && (
                                    <SearchContainer>
                                        <SearchIcon />
                                        <SearchOption
                                            placeholder="Search"
                                            name="searchItem"
                                            id="searchItem"
                                            onClick={() => {
                                                handleOpen(inputClicked);
                                            }}
                                            onChange={handleInputChange}
                                            autoFocus={true}
                                        />
                                    </SearchContainer>
                                )}
                                {state.filteredOptions.map((o, i) => {
                                    let selectedItem = false;
                                    if (value && value.length > 0) {
                                        if (value.indexOf(o) !== -1) selectedItem = true;
                                    }
                                    if (value && value[name] === o[name]) selectedItem = true;
                                    return (
                                        <Option
                                            key={i}
                                            {...props}
                                            onClick={() => {
                                                handleItemClick(o);
                                                clearSearchInput();
                                                if (!isManualClose) setOptionOpen((state) => !state);
                                            }}
                                            isLast={i === state.filteredOptions.length - 1}
                                            selectedItem={selectedItem}
                                        >
                                            <Typography variant="body1">{isRenderData ? renderData(o) : name ? o[name] : o}</Typography>
                                        </Option>
                                    );
                                })}
                            </OptionContainer>
                        ))}
                </Container>
            </Box>
        </ClickAwayListener>
    );
};

export default GlobalDropdown;

const Container = styled.div<ContainerProps>`
    position: relative;
    border: 1px solid ${(props) => (props.optionOpen ? colors.blue.main : colors.border)};
    color: ${colors.blue.main};
    background: ${colors.white};
    border-radius: 6px;
    min-height: 28px;
    padding: 0px 10px;
    width: 100%;
    min-width: ${(props) => (props.isFilter ? '300px' : props.width ? props.width : '200px')};
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    ${(props) =>
        props.disabled
            ? {
                  border: `1px solid ${colors.border}`,
                  color: colors.border,
                  img: { opacity: 0.3 },
                  svg: { path: { fill: colors.border } },
                  '&:hover': { cursor: 'default', border: `1px solid ${colors.border}` },
              }
            : {
                  '&:hover': {
                      cursor: 'pointer',
                      border: `1px solid ${colors.blue.main}`,
                      transition: 'all 0.2s',
                  },
              }}
    #arrow {
        transform: ${(props) => (props.optionOpen ? 'rotate(180deg)' : null)};
        transition: all 0.2s;
        min-width: 24px;
        min-height: 24px;
    }
`;

const OptionContainer = styled.div`
    position: absolute;
    top: 110%;
    left: 0;
    background-color: #fff;
    filter: drop-shadow(8px 16px 24px rgba(69, 79, 89, 0.04));
    color: ${colors.blue.main};
    border-radius: 0px 0px 6px 6px;
    max-height: 230px;
    overflow-y: scroll;
    width: 100%;
    overflow-x: hidden;
`;

const Option = styled.div<OptionProps>`
    border-radius: ${(props) => (props.isLast ? '0px 0px 6px 6px' : null)};
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 5px;
    background-color: ${(props) => (props.selectedItem ? colors.blue.light : '#fff')};

    img {
        opacity: 0;
        z-index: 95;
        padding-right: 12px;
    }

    p {
        padding: 10px 12px;
    }

    &:hover {
        background: ${colors.blue.light};
        border-radius: 5px;

        img {
            opacity: 1;
        }
    }
`;

const SearchContainer = styled.div`
    display: flex;
    align-items: center;
    border: 1px solid ${colors.blue.main};
    background-color: rgba(0, 124, 222, 0.1);
    margin: 5px;
    border-radius: 3px;
    padding: 2px 12px;
`;

const SearchOption = styled.input`
    width: 97%;
    margin: 5px;
    color: ${colors.blue.main};
    background-color: transparent;
    border: none;

    &:focus {
        outline: none;
    }

    &::placeholder {
        color: rgba(0, 124, 222, 0.6);
    }
`;

const SelectedItem = styled.span`
    background-color: ${colors.blue.main};
    color: #fff;
    font-size: 12px;
    border-radius: 15px;
    padding: 3px 10px;
    /* margin-right: 3px; */
    margin: 1.5px 3px 1.5px 0px;
    display: flex;
    align-items: center;

    svg {
        width: 15px;
        height: auto;
        margin-left: 2px;

        path {
            fill: #fff;
        }

        &:hover {
            opacity: 0.7;
            transition: all 0.3s;
            cursor: pointer;
        }
    }
`;

const FilterOption = styled.div`
    padding: 10px;
    text-transform: capitalize;

    div {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
    }

    span {
        border: 1px solid ${colors.grey.medium};
        color: ${colors.grey.dark};
        border-radius: 5px;
        padding: 2px 5px;
        margin: 5px;
        white-space: noWrap;

        &:hover {
            cursor: pointer;
            background-color: ${colors.blue.light};
            color: ${colors.blue.main};
            border-color: ${colors.blue.main};
        }
    }
`;
